import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Button, Typography, CircularProgress, Grid, Chip, Divider, Paper, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, List, ListItem, ListItemText, Snackbar, Alert } from '@mui/material';
import { campaignService, donationService } from '../services/api';
import { ArrowBack, InsertDriveFile, Delete, Replay } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import config, { getAssetUrl } from '../config';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/** Extract iframe src from map_embed_url (handles full iframe HTML paste or plain URL) */
function getMapEmbedSrc(value) {
  if (!value || typeof value !== 'string') return '';
  const s = value.trim();
  const srcMatch = s.match(/src\s*=\s*["']([^"']+)["']/i);
  const url = srcMatch ? srcMatch[1].trim() : (s.startsWith('http://') || s.startsWith('https://') ? s : s);
  return url;
}

/** Reject static map image URLs (e.g. Google Static Maps API) so we show embed only for interactive maps */
function isStaticMapOrImageUrl(url) {
  if (!url || typeof url !== 'string') return true;
  const lower = url.toLowerCase();
  if (lower.includes('staticmap') || lower.includes('static_map') || lower.includes('/api/staticmap')) return true;
  if (/\.(png|jpe?g|gif|webp)(\?|$)/i.test(url)) return true;
  return false;
}

/** Only embed URLs that are allowed to be framed (e.g. Google Maps embed, not regular maps link or static image) */
function isEmbeddableMapUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (isStaticMapOrImageUrl(url)) return false;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    const path = u.pathname.toLowerCase();
    if (host === 'www.google.com' || host === 'google.com') {
      return path.startsWith('/maps/embed');
    }
    if (host.includes('openstreetmap.org') || host.includes('maps.google.com')) return true;
    return true;
  } catch (_) {
    return false;
  }
}

const CampaignDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  useEffect(() => {
    fetchCampaignDetails();
    fetchDonations();
  }, [id]);

  const fetchCampaignDetails = async () => {
    try {
      setLoading(true);
      const res = await campaignService.getCampaignById(id);
      const campaignData = res.data?.data || res.data;
      setCampaign(campaignData);
    } catch (error) {
      console.error('Error fetching campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDonations = async () => {
    try {
      const res = await donationService.getAllDonations();
      const allDonations = res.data?.data || res.data || [];
      // Filter donations for this campaign
      const campaignDonations = Array.isArray(allDonations) ? allDonations.filter(d => d.campaign_id === parseInt(id)) : [];
      setDonations(campaignDonations);
    } catch (error) {
      console.error('Error fetching donations:', error);
    }
  };

  const changeCampaignStatus = async (status) => {
    if (!campaign) return;
    const isDelete = status === 'deleted';
    const confirmed = window.confirm(
      isDelete
        ? `Remove "${campaign.title}" from the website? It stays in the database and you can restart it later.`
        : `Restart "${campaign.title}"? It will show on the website again.`
    );
    if (!confirmed) return;
    try {
      setStatusBusy(true);
      await campaignService.updateCampaignStatus(campaign.id, { status });
      setToast({
        open: true,
        message: isDelete ? 'Campaign removed from the website' : 'Campaign restarted',
        type: 'success',
      });
      await fetchCampaignDetails();
    } catch (error) {
      setToast({
        open: true,
        message: error.response?.data?.error || 'Could not update the campaign',
        type: 'error',
      });
    } finally {
      setStatusBusy(false);
    }
  };

  if (loading && !campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
        <Container maxWidth={false}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/campaigns')}
            sx={{ color: '#2c5f2d', mb: 4, textTransform: 'none' }}
          >
            Back to Campaigns
          </Button>
          <Typography variant="h5" color="error">Campaign not found</Typography>
        </Container>
      </Box>
    );
  }

  const progressPercentage = campaign.target_amount > 0 
    ? (campaign.current_amount / campaign.target_amount) * 100 
    : 0;

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'archived': return 'default';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  const daysRemaining = campaign.end_date 
    ? Math.ceil((new Date(campaign.end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/campaigns')}
            sx={{ color: '#2c5f2d', mb: 2, textTransform: 'none' }}
          >
            Back to Campaigns
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                {campaign.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Campaign ID: {campaign.id} • Created on {new Date(campaign.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric'
                })}
                {campaign.category?.name ? ` • Category: ${campaign.category.name}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {campaign.status === 'deleted' ? (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Replay />}
                  disabled={statusBusy}
                  onClick={() => changeCampaignStatus('active')}
                  sx={{ backgroundColor: '#059669', textTransform: 'none' }}
                >
                  Restart
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<Delete />}
                  disabled={statusBusy}
                  onClick={() => changeCampaignStatus('deleted')}
                  sx={{ color: '#b42318', borderColor: '#b42318', textTransform: 'none' }}
                >
                  Delete
                </Button>
              )}
              <Chip 
                label={campaign.status.toUpperCase()} 
                color={getStatusColor(campaign.status)}
                sx={{ height: '40px', fontSize: '0.9rem', fontWeight: '600' }}
              />
            </Box>
          </Box>
        </Box>

        {/* Campaign Progress Section */}
        <Card sx={{ p: 4, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c' }}>
                Funding Progress
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', fontWeight: '600' }}>
                {progressPercentage.toFixed(1)}% funded
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(progressPercentage, 100)}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#e8ecf1',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#059669',
                  borderRadius: 4
                }
              }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                  TARGET AMOUNT
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c', mt: 0.5 }}>
                  ₹{campaign.target_amount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                  RAISED SO FAR
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669', mt: 0.5 }}>
                  ₹{campaign.current_amount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                  REMAINING
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  ₹{(campaign.target_amount - campaign.current_amount).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                  DAYS REMAINING
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: daysRemaining > 0 ? '#059669' : '#dc2626', mt: 0.5 }}>
                  {daysRemaining > 0 ? daysRemaining : 'Ended'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Campaign Details */}
          <Grid item xs={12} md={8}>
            {/* Campaign Description */}
            <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', lineHeight: 1.8 }}>
                {campaign.description}
              </Typography>
            </Card>

            {/* Campaign Content */}
            {campaign.content && (
              <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Campaign Details
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f8fafb', borderLeft: '4px solid #2c5f2d' }}>
                  <Typography variant="body2" sx={{ color: '#667085', lineHeight: 1.8 }}>
                    {campaign.content}
                  </Typography>
                </Paper>
              </Card>
            )}

            {/* Media Section */}
            {(campaign.image_url || campaign.video_url) && (
              <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Media
                </Typography>
                <Grid container spacing={2}>
                  {campaign.image_url && (
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          CAMPAIGN IMAGE
                        </Typography>
                        <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', backgroundColor: '#f8fafb', height: 250 }}>
                          <img 
                            src={getAssetUrl(campaign.image_url)} 
                            alt={campaign.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {campaign.video_url && (
                    <Grid item xs={12} sm={campaign.image_url ? 6 : 12}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          CAMPAIGN VIDEO
                        </Typography>
                        <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', backgroundColor: '#000', height: 250 }}>
                          <iframe
                            width="100%"
                            height="250"
                            src={getAssetUrl(campaign.video_url)}
                            title={campaign.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Card>
            )}

            {/* Documents */}
            {campaign.documents && campaign.documents.length > 0 && (
              <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Documents
                </Typography>
                <List dense>
                  {campaign.documents.map((doc) => (
                    <ListItem key={doc.id} component="a" href={getAssetUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'inherit' }}>
                      <InsertDriveFile sx={{ mr: 1, color: '#667085' }} />
                      <ListItemText primary={doc.title} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}

            {/* Map */}
            {(campaign.map_embed_url || (campaign.map_lat && campaign.map_lng)) && (
              <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Map
                </Typography>
                {campaign.map_address && (
                  <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>{campaign.map_address}</Typography>
                )}
                {(() => {
                  const mapSrc = getMapEmbedSrc(campaign.map_embed_url);
                  if (mapSrc && isEmbeddableMapUrl(mapSrc)) {
                    return (
                      <Box sx={{ width: '100%', height: 350, borderRadius: 1, overflow: 'hidden' }}>
                        <iframe title="Campaign map" src={mapSrc} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                      </Box>
                    );
                  }
                  if (campaign.map_embed_url || mapSrc) {
                    const openUrl = mapSrc || campaign.map_embed_url;
                    return (
                      <Box sx={{ width: '100%', minHeight: 120, borderRadius: 1, bgcolor: '#f8fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                          This link is not an embeddable map (e.g. use Google Maps → Share → Embed a map). Open in new tab to view.
                        </Typography>
                        <Button component="a" href={openUrl} target="_blank" rel="noopener noreferrer" variant="outlined" size="small">
                          Open map in new tab
                        </Button>
                      </Box>
                    );
                  }
                  if (campaign.map_lat && campaign.map_lng) {
                    const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(campaign.map_lat + ',' + campaign.map_lng)}`;
                    return (
                      <Box sx={{ width: '100%', minHeight: 120, borderRadius: 1, bgcolor: '#f8fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Lat: {campaign.map_lat}, Lng: {campaign.map_lng}. Add an embed URL in Edit to show map, or open coordinates below.
                        </Typography>
                        <Button component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer" variant="outlined" size="small">
                          Open in Google Maps
                        </Button>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Card>
            )}

            {/* Contributors Section */}
            {donations.length > 0 && (
              <Card sx={{ p: 3, mt: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Contributors ({donations.length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Donor Name</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Payment Method</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Payment Date</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {donations.map((donation, index) => (
                        <TableRow key={index} sx={{ borderBottom: '1px solid #e8ecf1' }}>
                          <TableCell sx={{ color: '#1a202c' }}>
                            {donation.name || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: '#667085', fontSize: '0.85rem' }}>
                            {donation.email || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: '600', color: '#059669' }}>
                            ₹{donation.amount ? donation.amount.toLocaleString() : '0'}
                          </TableCell>
                          <TableCell sx={{ color: '#667085' }}>
                            <Chip 
                              label={donation.payment_method || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#667085' }}>
                            {donation.created_at 
                              ? new Date(donation.created_at).toLocaleDateString('en-US')
                              : 'N/A'
                            }
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={donation.payment_status || 'pending'}
                              size="small"
                              color={donation.payment_status === 'completed' ? 'success' : donation.payment_status === 'failed' ? 'error' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Grid>

          {/* Right Column - Campaign Info */}
          <Grid item xs={12} md={4}>
            {/* Campaign Timeline */}
            <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Timeline
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    START DATE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(campaign.start_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    END DATE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(campaign.end_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    DURATION
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {Math.ceil((new Date(campaign.end_date) - new Date(campaign.start_date)) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Campaign Status Card */}
            <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Campaign Status
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CURRENT STATUS
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      color={getStatusColor(campaign.status)}
                      size="medium"
                      sx={{ fontWeight: '600' }}
                    />
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    SLUG
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                    {campaign.slug}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Campaign Metadata */}
            <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CAMPAIGN ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                    {campaign.id}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    CREATED DATE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(campaign.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignDetailPage;
